import React, { useEffect, useState } from "react";
import { GrGallery } from "react-icons/gr";
import PhoneNumberField from "../../components/FormComponents/PhoneNumberField";
import InputField from "../../components/FormComponents/InputField";
import InputSelecter from "../../components/FormComponents/InputSelecter";
import { Divider, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { getDropdown } from "../../Redux/Actions/UsersProfileAction";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import SearchableSelect from "../../components/FormComponents/SearchableSelect";

const PersonalDetails = ({
  formData,
  updateFormData,
  nextStep,
  highschoolList,
  setHighschoolList,
  educationList,
  setEducationList,
  awardsList,
  setAwardsList,
  rewardsList,
  setRewardsList,
  medalsList,
  setMedalsList,
  certificationsList,
  setCertificationsList,
  images,
  setImages,
  occupationList,
  setOccupationList,
  handlePhoneNumberChange,
  handleCodeChange,
  handleCountryChange,
  DOB,
  handleUserDOBChange,
  userHeight,
  handleUserHeightChange,
  setDOB
}) => {
  const dispatch = useDispatch();
  const [castData, setCastData] = useState([]);
  const [religionData, setReligionData] = useState([]);
  const [dietData, setDietData] = useState([]);
  const [maritalData, setMaritalData] = useState([]);
  
  // State for error messages
  const [errors, setErrors] = useState({});

  // Month options for dropdown
  const monthOptions = [
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" }
  ];

  useEffect(() => {
    const fetchDropDown = async () => {
      try {
        const res = await dispatch(getDropdown());
        if (res?.data?.data?.cast) {
          console.log("API Response:", res.data.data);
          setCastData(res.data.data.cast);
          setReligionData(res.data.data.religion);
          setDietData(res.data.data.diet);
          setMaritalData(res.data.data.marital);
        } else {
          console.log("Error: Cast data not found in API response");
          toast.error("Failed to fetch cast data.");
        }
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
        toast.error(error?.response?.data?.message || "Failed to fetch data.");
      }
    };

    fetchDropDown();
  }, [dispatch]);

  // Validation function
  const validateForm = () => {
    const newErrors = {};

    // Image validation
    if (images.filter(img => img !== null).length === 0) {
      newErrors.images = "Please upload at least one image";
    }

    // Gender validation
    if (!formData.gender) {
      newErrors.gender = "Please select your gender";
    }

    // Date of Birth validation
    if (!DOB.dobDay || !DOB.dobMonth || !DOB.dobYear) {
      newErrors.dob = "Please enter complete date of birth";
    } else {
      const day = parseInt(DOB.dobDay);
      const month = parseInt(DOB.dobMonth);
      const year = parseInt(DOB.dobYear);
      
      if (day < 1 || day > 31) newErrors.dobDay = "Invalid day";
      if (month < 1 || month > 12) newErrors.dobMonth = "Invalid month";
      if (year < 1900 || year > new Date().getFullYear()) newErrors.dobYear = "Invalid year";
    }

    // Religion validation
    if (!formData.religion) {
      newErrors.religion = "Please select your religion";
    }

    // Cast validation
    if (!formData.cast) {
      newErrors.cast = "Please select your cast";
    }

    // Height validation
    if (!userHeight.heightFt || !userHeight.heightIn) {
      newErrors.height = "Please enter both feet and inches";
    } else {
      const feet = parseInt(userHeight.heightFt);
      const inches = parseInt(userHeight.heightIn);
      if (feet < 3 || feet > 8) newErrors.heightFt = "Invalid height (3-8 ft)";
      if (inches < 0 || inches > 11) newErrors.heightIn = "Invalid inches (0-11)";
    }

    // Weight validation
    if (!formData.weight) {
      newErrors.weight = "Please enter your weight";
    } else if (parseFloat(formData.weight) < 20 || parseFloat(formData.weight) > 300) {
      newErrors.weight = "Please enter valid weight (20-300 kg)";
    }

    // Phone number validation
    if (!formData.phone_number) {
      newErrors.phone_number = "Please enter your phone number";
    } else if (formData.phone_number.length < 10) {
      newErrors.phone_number = "Please enter valid phone number";
    }

    // Education validation (only for college/university)
    educationList.forEach((edu, index) => {
      if (edu.degree && (!edu.from_year || !edu.to_year)) {
        newErrors[`education_${index}`] = "Please fill all education fields or remove empty entries";
      }
      if (edu.from_year && edu.to_year && parseInt(edu.from_year) > parseInt(edu.to_year)) {
        newErrors[`education_years_${index}`] = "From year cannot be greater than to year";
      }
    });

    // Occupation validation (required field)
    if (occupationList.length === 0) {
      newErrors.occupation = "Please add at least one occupation";
    } else {
      occupationList.forEach((occ, index) => {
        if (!occ.role || !occ.from_year || !occ.to_year) {
          newErrors[`occupation_${index}`] = "Please fill all occupation fields";
        }
        if (occ.from_year && occ.to_year && parseInt(occ.from_year) > parseInt(occ.to_year)) {
          newErrors[`occupation_years_${index}`] = "From year cannot be greater than to year";
        }
      });
    }

    // City validation
    if (!formData.city) {
      newErrors.city = "Please enter your city";
    }

    // Country validation
    if (!formData.country) {
      newErrors.country = "Please enter your country";
    }

    // #Of Years In Country validation (required field)
    if (!formData.years_in_country) {
      newErrors.years_in_country = "Please enter number of years in country";
    } else {
      const years = parseInt(formData.years_in_country);
      if (isNaN(years) || years < 0 || years > 100) {
        newErrors.years_in_country = "Please enter a valid number of years (0-100)";
      }
    }

    // Visa Status validation (required field)
    if (!formData.visa_status) {
      newErrors.visa_status = "Please enter your visa status";
    } else if (formData.visa_status.length < 2) {
      newErrors.visa_status = "Visa status should be at least 2 characters long";
    } else if (formData.visa_status.length > 100) {
      newErrors.visa_status = "Visa status should not exceed 100 characters";
    }

    // Nationality validation
    if (!formData.nationality) {
      newErrors.nationality = "Please enter your nationality";
    }

    // Diet validation
    if (!formData.diet) {
      newErrors.diet = "Please select your diet";
    }

    // Marital status validation
    if (!formData.marital_status) {
      newErrors.marital_status = "Please select your marital status";
    }

    // Live with family validation
    if (formData.live_with_family === "") {
      newErrors.live_with_family = "Please select if you live with family";
    }

    // Hobbies validation
    if (!formData.hobbies) {
      newErrors.hobbies = "Please enter your hobbies";
    }

    // Extra skills validation
    if (!formData.extra_skills) {
      newErrors.extra_skills = "Please enter your extra skills";
    }

    // Validate dynamic lists (rewards, awards, medals, certifications)
    [rewardsList, awardsList, medalsList, certificationsList].forEach((list, listIndex) => {
      const listNames = ['rewards', 'awards', 'medals', 'certifications'];
      list.forEach((item, index) => {
        if (item.title && !item.year) {
          newErrors[`${listNames[listIndex]}_${index}`] = `Please enter year for ${listNames[listIndex]}`;
        }
        if (!item.title && item.year) {
          newErrors[`${listNames[listIndex]}_${index}_title`] = `Please enter title for ${listNames[listIndex]}`;
        }
      });
    });

    // Highschool validation (optional - no validation for schools)
    // Removed school validation as per requirement

    return newErrors;
  };

  const handleNext = (e) => {
    e.preventDefault();
    
    // Validate form
    const newErrors = validateForm();
    setErrors(newErrors);

    // Check if there are any errors
    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fix the errors before proceeding");
      return;
    }

    // Format and update form data if validation passes
    if (DOB.dobYear.trim() && DOB.dobMonth.trim() && DOB.dobDay.trim()) {
      const date_of_birth = `${DOB.dobYear.trim()}-${DOB.dobMonth
        .trim()
        .padStart(2, "0")}-${DOB.dobDay.trim().padStart(2, "0")}`;

      updateFormData({ target: { name: "date_of_birth", value: date_of_birth } });
    }

    if (userHeight.heightFt.trim() && userHeight.heightIn.trim()) {
      updateFormData({ target: { name: "height", value: userHeight.heightFt.concat('.', userHeight.heightIn) } });
    }
    
    nextStep();
  };

  // Handle month selection change
  const handleMonthChange = (e) => {
    const { name, value } = e.target;
    setDOB((prev) => ({ ...prev, [name]: value }));
    // Clear error when user selects a month
    if (errors.dobMonth) {
      setErrors(prev => ({ ...prev, dobMonth: "" }));
    }
  };

  // Helper function to update form data and clear errors
  const handleUpdateFormData = (e) => {
    updateFormData(e);
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors(prev => ({ ...prev, [e.target.name]: "" }));
    }
  };

  // Helper function for dynamic list changes
  const handleDynamicListChange = (list, setList, index, field, value) => {
    const newList = [...list];
    newList[index] = { ...newList[index], [field]: value };
    setList(newList);
    
    // Clear related errors
    const errorKey = `${field}_${index}`;
    if (errors[errorKey]) {
      setErrors(prev => ({ ...prev, [errorKey]: "" }));
    }
  };

  return (
    <div className="w-full md:w-xl max-w-2xl mx-auto p-4">
      <div className="max-w-2xl mx-auto max-h-[70vh] lg:max-h-[70vh] overflow-y-auto px-2">
        {/* Image Upload Section */}
        <label className="block font-medium mb-2">Upload Your Images<span className="text-[14px] text-[#E72848]">*</span></label>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4">
          {images.map((img, index) => (
            <label key={index} className="cursor-pointer">
              <div className="w-full aspect-square border border-dashed border-gray-300 rounded-xl flex items-center justify-center bg-gray-100">
                {img ? (
                  <img
                    src={img && URL.createObjectURL(img)}
                    alt="Uploaded"
                    className="w-full h-full object-cover rounded-xl"
                  />
                ) : (
                  <span className="text-gray-400 text-2xl">
                    <GrGallery />
                  </span>
                )}
              </div>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const newImages = [...images];
                    newImages[index] = file;
                    setImages(newImages);
                    // Clear image error when user uploads an image
                    if (errors.images) {
                      setErrors(prev => ({ ...prev, images: "" }));
                    }
                  }
                }}
              />
            </label>
          ))}
        </div>
        {errors.images && <p className="text-red-500 text-sm mb-4">{errors.images}</p>}

        {/* Gender Selection */}
        <div className="mb-4">
          <label className="block font-medium mb-1">Gender<span className="text-[14px] text-[#E72848]">*</span></label>
          <RadioGroup
            row
            name="gender"
            value={formData.gender}
            onChange={handleUpdateFormData}
          >
            <FormControlLabel
              value="Male"
              control={<Radio sx={{ '&.Mui-checked': { color: '#E72848' } }} />}
              label="Male"
            />
            <FormControlLabel
              value="Female"
              control={<Radio sx={{ '&.Mui-checked': { color: '#E72848' } }} />}
              label="Female"
            />
          </RadioGroup>
          {errors.gender && <p className="text-red-500 text-sm mt-1">{errors.gender}</p>}
        </div>

        {/* Date of Birth Inputs */}
        <label className="block font-medium mb-1">Date of Birth<span className="text-[14px] text-[#E72848]">*</span></label>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div>
            <InputField
              type="number"
              label="Day"
              name="dobDay"
              placeholder="DD"
              value={DOB.dobDay}
              onChange={handleUserDOBChange}
              maxLength={2}
              error={errors.dobDay}
            />
            {errors.dobDay && <p className="text-red-500 text-sm mt-1">{errors.dobDay}</p>}
          </div>
          <div>
            <InputSelecter
              label="Month"
              name="dobMonth"
              value={DOB.dobMonth}
              onChange={handleMonthChange}
              options={monthOptions}
              placeholder="MM"
              error={errors.dobMonth}
            />
            {errors.dobMonth && <p className="text-red-500 text-sm mt-1">{errors.dobMonth}</p>}
          </div>
          <div>
            <InputField
              type="number"
              label="Year"
              name="dobYear"
              placeholder="YYYY"
              value={DOB.dobYear}
              onChange={handleUserDOBChange}
              maxLength={4}
              error={errors.dobYear}
            />
            {errors.dobYear && <p className="text-red-500 text-sm mt-1">{errors.dobYear}</p>}
          </div>
        </div>
        {errors.dob && <p className="text-red-500 text-sm -mt-2 mb-4">{errors.dob}</p>}

        {/* Religion & Cast */}
        <div className="mb-4">
          <InputSelecter
            label="Your Religion"
            name="religion"
            value={formData.religion}
            onChange={handleUpdateFormData}
            options={religionData && religionData.map((item) => ({
              label: item.text,
              value: item.text
            }))}
            error={errors.religion}
          />
          {errors.religion && <p className="text-red-500 text-sm mt-1">{errors.religion}</p>}
        </div>
       
        <div className="mb-4">
          <SearchableSelect
            label="Cast"
            name="cast"
            value={formData.cast}
            onChange={handleUpdateFormData}
            options={Array.isArray(castData) ? castData.map((item) => ({
              label: item.text || item.label || String(item),
              value: item.text || item.value || String(item)
            })) : []}
            placeholder="Search cast..."
            error={errors.cast}
          />
          {errors.cast && <p className="text-red-500 text-sm mt-1">{errors.cast}</p>}
        </div>

        {/* Height & Weight */}
        <div className="grid grid-cols-3 gap-4 my-4 w-full">
          <div>
            <InputField
              type="number"
              label="Height(ft)"
              name="heightFt"
              placeholder="ft"
              value={userHeight.heightFt}
              onChange={handleUserHeightChange}
              error={errors.heightFt}
            />
          </div>
          <div className="mt-auto">
            <InputField
              label="Height(in)"
              type="number"
              name="heightIn"
              placeholder="in"
              value={userHeight.heightIn}
              onChange={handleUserHeightChange}
              error={errors.heightIn}
            />
          </div>
          <div>
            <InputField
              label="Weight"
              name="weight"
              placeholder="kg"
              value={formData.weight}
              onChange={handleUpdateFormData}
              error={errors.weight}
            />
          </div>
        </div>
        {errors.height && <p className="text-red-500 text-sm -mt-2 mb-4">{errors.height}</p>}
        {errors.weight && <p className="text-red-500 text-sm -mt-2 mb-4">{errors.weight}</p>}

        {/* Phone Number */}
        <div className="mb-4">
          <div className="mui-tel-input">
            <label className="block mb-1 text-sm">Phone Number<span className="text-[14px] text-[#E72848]">*</span></label>
            <PhoneNumberField
              value={formData.phone_number}
              setPhoneNumber={(newPhone) =>
                handlePhoneNumberChange("phone_number", newPhone)
              }
              disabled={false}
              code={formData.country_code}
              setCode={(newCode) => handleCodeChange("country_code", newCode)}
              error={errors.phone_number}
            />
          </div>
          {errors.phone_number && <p className="text-red-500 text-sm mt-1">{errors.phone_number}</p>}
        </div>

        {/* Education */}
        <div className="mb-4">
          <label className="block font-semibold mb-1">Education</label>
          {educationList.map((edu, index) => (
            <div key={index} className="mb-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-4">
                <div>
                  <InputField
                    label="Degree"
                    name="degree"
                    placeholder="Enter Degree name"
                    value={edu.degree}
                    onChange={(e) => handleDynamicListChange(educationList, setEducationList, index, 'degree', e.target.value)}
                  />
                </div>
                <div>
                  <div className="flex gap-2">
                    <InputField
                      label="Year From"
                      name="from_year"
                      placeholder="0000"
                      value={edu.from_year}
                      onChange={(e) => handleDynamicListChange(educationList, setEducationList, index, 'from_year', e.target.value)}
                      maxLength={4}
                      required={false}
                    />
                    <span className="flex items-center mt-8">to</span>
                    <InputField
                      label="Year To"
                      name="to_year"
                      placeholder="0000"
                      value={edu.to_year}
                      onChange={(e) => handleDynamicListChange(educationList, setEducationList, index, 'to_year', e.target.value)}
                      maxLength={4}
                      required={false}
                    />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div>
                  <InputField
                    label="University Name"
                    name="university_name"
                    placeholder="Enter University name"
                    value={edu.university_name}
                    required={false}
                    onChange={(e) => handleDynamicListChange(educationList, setEducationList, index, 'university_name', e.target.value)}
                  />
                </div>
                <div>
                  <InputField
                    label="City"
                    name="city"
                    required={false}
                    placeholder="Enter city name"
                    value={edu.city}
                    onChange={(e) => handleDynamicListChange(educationList, setEducationList, index, 'city', e.target.value)}
                  />
                </div>
              </div>

              {errors[`education_${index}`] && (
                <p className="text-red-500 text-sm mt-1">{errors[`education_${index}`]}</p>
              )}
              {errors[`education_years_${index}`] && (
                <p className="text-red-500 text-sm mt-1">{errors[`education_years_${index}`]}</p>
              )}

              {index > 0 && (
                <div className="flex justify-center">
                  <button
                    className="text-red-600 text-sm mt-2 underline cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      const newEdu = [...educationList];
                      newEdu.splice(index, 1);
                      setEducationList(newEdu);
                    }}
                  >
                    Remove Education
                  </button>
                </div>
              )}
            </div>
          ))}

          <div className="flex justify-center">
            <button
              className="text-blue-600 text-sm mt-2 underline cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                setEducationList([
                  ...educationList,
                  { degree: "", from_year: "", to_year: "", university_name: "", city: "" },
                ]);
              }}
            >
              Add More Education
            </button>
          </div>

          {/* Highschool Section (Optional - No Validation) */}
          <label className="block font-semibold mt-4 mb-2">High School (Optional)</label>
          {highschoolList.map((rew, index) => {
            return (
              <div key={index}>
                <div className="grid grid-cols-1 gap-4 my-2">
                  <div>
                    <InputField
                      label="School Name"
                      name="school_name"
                      placeholder="Enter School name"
                      required={false}
                      value={highschoolList[index]?.school_name || ""}
                      onChange={(e) => handleDynamicListChange(highschoolList, setHighschoolList, index, 'school_name', e.target.value)}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-4">
                  <div className="flex gap-2 w-full">
                    <InputField
                      label="Year From"
                      name="from_year"
                      placeholder="0000"
                      required={false}
                      value={highschoolList[index]?.from_year || ""}
                      onChange={(e) => handleDynamicListChange(highschoolList, setHighschoolList, index, 'from_year', e.target.value)}
                      maxLength={4}
                    />
                    <span className="flex items-center mt-8">to</span>
                    <InputField
                      label="Year To"
                      name="to_year"
                      placeholder="0000"
                      required={false}
                      value={highschoolList[index]?.to_year || ""}
                      onChange={(e) => handleDynamicListChange(highschoolList, setHighschoolList, index, 'to_year', e.target.value)}
                      maxLength={4}
                    />
                  </div>
                  <div>
                    <InputField
                      label="City"
                      name="city"
                      required={false}
                      placeholder="Enter City name"
                      value={highschoolList[0]?.city || ""}
                      onChange={(e) => handleDynamicListChange(highschoolList, setHighschoolList, 0, 'city', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Rewards */}
        <label className="block font-semibold mb-2">Rewards</label>
        {rewardsList.map((rew, index) => {
          return (
            <div key={index} className="mb-4">
              <div className="grid grid-cols-2 gap-4 my-2">
                <div>
                  <InputField
                    label="Title"
                    name="title"
                    placeholder="Enter Rewards Title"
                    required={false}
                    value={rew.title}
                    onChange={(e) => handleDynamicListChange(rewardsList, setRewardsList, index, 'title', e.target.value)}
                  />
                </div>
                <div>
                  <InputField
                    label="Year"
                    name="year"
                    required={false}
                    placeholder="Enter Rewards Year"
                    value={rew.year}
                    onChange={(e) => handleDynamicListChange(rewardsList, setRewardsList, index, 'year', e.target.value)}
                  />
                </div>
              </div>
              
              {errors[`rewards_${index}`] && (
                <p className="text-red-500 text-sm mt-1">{errors[`rewards_${index}`]}</p>
              )}
              {errors[`rewards_${index}_title`] && (
                <p className="text-red-500 text-sm mt-1">{errors[`rewards_${index}_title`]}</p>
              )}

              {index > 0 && (
                <div className="flex justify-center">
                  <button
                    className="text-red-600 text-sm mt-2 underline cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      const newEdu = [...rewardsList];
                      newEdu.splice(index, 1);
                      setRewardsList(newEdu);
                    }}
                  >
                    Remove Reward
                  </button>
                </div>
              )}
            </div>
          )
        })}
        <div className="flex justify-center">
          <button
            className="text-blue-600 text-sm mt-1 underline cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              setRewardsList([
                ...rewardsList,
                { title: "", year: "" },
              ]);
            }}
          >
            Add More Rewards
          </button>
        </div>

        {/* Awards */}
        <label className="block font-semibold mt-2">Awards</label>
        {awardsList.map((awa, index) => {
          return (
            <div key={index} className="mb-4">
              <div className="grid grid-cols-2 gap-4 my-2">
                <div>
                  <InputField
                    label="Title"
                    name="title"
                    placeholder="Enter Awards Title"
                    required={false}
                    value={awa.title}
                    onChange={(e) => handleDynamicListChange(awardsList, setAwardsList, index, 'title', e.target.value)}
                  />
                </div>
                <div>
                  <InputField
                    label="Year"
                    name="year"
                    required={false}
                    placeholder="Enter Awards Year"
                    value={awa.year}
                    onChange={(e) => handleDynamicListChange(awardsList, setAwardsList, index, 'year', e.target.value)}
                  />
                </div>
              </div>
              
              {errors[`awards_${index}`] && (
                <p className="text-red-500 text-sm mt-1">{errors[`awards_${index}`]}</p>
              )}
              {errors[`awards_${index}_title`] && (
                <p className="text-red-500 text-sm mt-1">{errors[`awards_${index}_title`]}</p>
              )}

              {index > 0 && (
                <div className="flex justify-center">
                  <button
                    className="text-red-600 text-sm mt-2 underline cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      const newEdu = [...awardsList];
                      newEdu.splice(index, 1);
                      setAwardsList(newEdu);
                    }}
                  >
                    Remove Award
                  </button>
                </div>
              )}
            </div>
          )
        })}
        <div className="flex justify-center">
          <button
            className="text-blue-600 text-sm mt-1 underline cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              setAwardsList([
                ...awardsList,
                { title: "", year: "" },
              ]);
            }}
          >
            Add More Awards
          </button>
        </div>

        {/* Medals */}
        <label className="block font-semibold mt-2">Medals</label>
        {medalsList.map((med, index) => {
          return (
            <div key={index} className="mb-4">
              <div className="grid grid-cols-2 gap-4 my-2">
                <div>
                  <InputField
                    label="Title"
                    name="title"
                    placeholder="Enter Medals Title"
                    required={false}
                    value={med.title}
                    onChange={(e) => handleDynamicListChange(medalsList, setMedalsList, index, 'title', e.target.value)}
                  />
                </div>
                <div>
                  <InputField
                    label="Year"
                    name="year"
                    required={false}
                    placeholder="Enter Medals Year"
                    value={med.year}
                    onChange={(e) => handleDynamicListChange(medalsList, setMedalsList, index, 'year', e.target.value)}
                  />
                </div>
              </div>
              
              {errors[`medals_${index}`] && (
                <p className="text-red-500 text-sm mt-1">{errors[`medals_${index}`]}</p>
              )}
              {errors[`medals_${index}_title`] && (
                <p className="text-red-500 text-sm mt-1">{errors[`medals_${index}_title`]}</p>
              )}

              {index > 0 && (
                <div className="flex justify-center">
                  <button
                    className="text-red-600 text-sm mt-2 underline cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      const newEdu = [...medalsList];
                      newEdu.splice(index, 1);
                      setMedalsList(newEdu);
                    }}
                  >
                    Remove Medal
                  </button>
                </div>
              )}
            </div>
          )
        })}
        <div className="flex justify-center">
          <button
            className="text-blue-600 text-sm mt-1 underline cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              setMedalsList([
                ...medalsList,
                { title: "", year: "" },
              ]);
            }}
          >
            Add More Medals
          </button>
        </div>

        {/* Certifications */}
        <label className="block font-semibold mt-2">Certifications</label>
        {certificationsList.map((cer, index) => {
          return (
            <div key={index} className="mb-4">
              <div className="grid grid-cols-2 gap-4 my-2">
                <div>
                  <InputField
                    label="Title"
                    name="title"
                    placeholder="Enter Certifications Title"
                    required={false}
                    value={cer.title}
                    onChange={(e) => handleDynamicListChange(certificationsList, setCertificationsList, index, 'title', e.target.value)}
                  />
                </div>
                <div>
                  <InputField
                    label="Year"
                    name="year"
                    required={false}
                    placeholder="Enter Certifications Year"
                    value={cer.year}
                    onChange={(e) => handleDynamicListChange(certificationsList, setCertificationsList, index, 'year', e.target.value)}
                  />
                </div>
              </div>
              
              {errors[`certifications_${index}`] && (
                <p className="text-red-500 text-sm mt-1">{errors[`certifications_${index}`]}</p>
              )}
              {errors[`certifications_${index}_title`] && (
                <p className="text-red-500 text-sm mt-1">{errors[`certifications_${index}_title`]}</p>
              )}

              {index > 0 && (
                <div className="flex justify-center">
                  <button
                    className="text-red-600 text-sm mt-2 underline cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      const newEdu = [...certificationsList];
                      newEdu.splice(index, 1);
                      setCertificationsList(newEdu);
                    }}
                  >
                    Remove Certification
                  </button>
                </div>
              )}
            </div>
          )
        })}
        <div className="flex justify-center">
          <button
            className="text-blue-600 text-sm mt-1 underline cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              setCertificationsList([
                ...certificationsList,
                { title: "", year: "" },
              ]);
            }}
          >
            Add More Certifications
          </button>
        </div>

        <Divider sx={{ my: 2 }} />

        {/* Occupation */}
        <div className="mb-4">
          <label className="block font-semibold mb-1">Occupation<span className="text-[14px] text-[#E72848]">*</span></label>
          {occupationList.length === 0 && errors.occupation && (
            <p className="text-red-500 text-sm mb-2">{errors.occupation}</p>
          )}
          {occupationList.map((occ, index) => (
            <div key={index} className="mb-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-4 mb-2">
                <div>
                  <InputField
                    label="Role"
                    name="role"
                    placeholder="Enter Your Role"
                    value={occ.role}
                    onChange={(e) => handleDynamicListChange(occupationList, setOccupationList, index, 'role', e.target.value)}
                    error={errors[`occupation_${index}`]}
                  />
                </div>
                <div>
                  <div className="flex gap-2">
                    <InputField
                      label="Year From"
                      name="from_year"
                      placeholder="0000"
                      value={occ.from_year}
                      onChange={(e) => handleDynamicListChange(occupationList, setOccupationList, index, 'from_year', e.target.value)}
                      maxLength={4}
                      required={false}
                      error={errors[`occupation_${index}`]}
                    />
                    <span className="flex items-center mt-8">to</span>
                    <InputField
                      label="Year To"
                      name="to_year"
                      placeholder="0000"
                      value={occ.to_year}
                      onChange={(e) => handleDynamicListChange(occupationList, setOccupationList, index, 'to_year', e.target.value)}
                      maxLength={4}
                      required={false}
                      error={errors[`occupation_${index}`]}
                    />
                  </div>
                </div>
              </div>
              <div>
                <InputField
                  label="Company Name"
                  name="company_name"
                  placeholder="Enter Company Name"
                  value={occ.company_name}
                  onChange={(e) => handleDynamicListChange(occupationList, setOccupationList, index, 'company_name', e.target.value)}
                  required={false}
                />
              </div>
              
              {errors[`occupation_${index}`] && (
                <p className="text-red-500 text-sm mt-1">{errors[`occupation_${index}`]}</p>
              )}
              {errors[`occupation_years_${index}`] && (
                <p className="text-red-500 text-sm mt-1">{errors[`occupation_years_${index}`]}</p>
              )}

              {index > 0 && (
                <div className="flex justify-center">
                  <button
                    className="text-red-600 text-sm mt-2 underline cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      const newOcc = [...occupationList];
                      newOcc.splice(index, 1);
                      setOccupationList(newOcc);
                    }}
                  >
                    Remove Occupation
                  </button>
                </div>
              )}
            </div>
          ))}
          <div className="flex justify-center">
            <button
              className="text-blue-600 text-sm my-2 mb-4 underline cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                setOccupationList([...occupationList, { role: "", from_year: "", to_year: "", company_name: "" }]);
              }}
            >
              Add More Occupation
            </button>
          </div>
        </div>

        <Divider sx={{ my: 2 }} />

        {/* Location */}
        <div className="mb-4">
          <InputField
            label="City"
            name="city"
            placeholder="Enter City"
            value={formData.city}
            onChange={handleUpdateFormData}
            error={errors.city}
          />
          {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
        </div>
        <div className="mb-4">
          <InputField
            label="Country"
            name="country"
            placeholder="Enter Country"
            value={formData.country}
            onChange={handleUpdateFormData}
            error={errors.country}
          />
          {errors.country && <p className="text-red-500 text-sm mt-1">{errors.country}</p>}
        </div>

        {/* Additional fields */}
        <div className="mb-4 grid grid-cols-2 gap-4">
          <div className="col-span-2 sm:col-span-1">
            <InputField
              label="#Of Years In Country"
              name="years_in_country"
              placeholder="e.g., 4"
              value={formData.years_in_country}
              onChange={handleUpdateFormData}
              required={true}
              error={errors.years_in_country}
            />
            {errors.years_in_country && <p className="text-red-500 text-sm mt-1">{errors.years_in_country}</p>}
          </div>
          <div className="col-span-2 sm:col-span-1">
            <InputField
              label="Visa Status"
              name="visa_status"
              placeholder="H1B (green card filing in process)"
              value={formData.visa_status}
              onChange={handleUpdateFormData}
              required={true}
              error={errors.visa_status}
            />
            {errors.visa_status && <p className="text-red-500 text-sm mt-1">{errors.visa_status}</p>}
          </div>
        </div>
        <div className="mb-4">
          <InputField
            label="Nationality"
            name="nationality"
            placeholder="American"
            value={formData.nationality}
            onChange={handleUpdateFormData}
            error={errors.nationality}
          />
          {errors.nationality && <p className="text-red-500 text-sm mt-1">{errors.nationality}</p>}
        </div>

        {/* Diet & Marital Status */}
        <div className="mb-4">
          <InputSelecter
            label="Your Diet"
            name="diet"
            value={formData.diet}
            onChange={handleUpdateFormData}
            options={dietData && dietData.map((item) => ({
              label: item.text,
              value: item.text
            }))}
            error={errors.diet}
          />
          {errors.diet && <p className="text-red-500 text-sm mt-1">{errors.diet}</p>}
        </div>
        <div className="mb-4">
          <InputSelecter
            label="Marital Status"
            name="marital_status"
            value={formData.marital_status}
            onChange={handleUpdateFormData}
            options={maritalData && maritalData.map((item) => ({
              label: item.text,
              value: item.text
            }))}
            error={errors.marital_status}
          />
          {errors.marital_status && <p className="text-red-500 text-sm mt-1">{errors.marital_status}</p>}
        </div>

        {/* Live with Family */}
        <div className="mb-4">
          <label className="block font-medium mb-1">
            You Live with your family?<span className="text-[14px] text-[#E72848]">*</span>
          </label>
          <div className="flex gap-4">
            <RadioGroup
              row
              name="live_with_family"
              value={formData.live_with_family}
              onChange={handleUpdateFormData}
            >
              <FormControlLabel value="true" control={<Radio sx={{ '&.Mui-checked': { color: '#E72848' } }} />} label="Yes" />
              <FormControlLabel value="false" control={<Radio sx={{ '&.Mui-checked': { color: '#E72848' } }} />} label="No" />
            </RadioGroup>
          </div>
          {errors.live_with_family && <p className="text-red-500 text-sm mt-1">{errors.live_with_family}</p>}
        </div>

        {/* Hobbies & Extra Skills */}
        <div className="mb-4">
          <InputField
            label="Hobbies"
            name="hobbies"
            placeholder="Yoga, Movies, Cricket, etc."
            value={formData.hobbies}
            onChange={handleUpdateFormData}
            error={errors.hobbies}
          />
          {errors.hobbies && <p className="text-red-500 text-sm mt-1">{errors.hobbies}</p>}
        </div>
        <div className="mb-4">
          <InputField
            label="Extra Skills"
            name="extra_skills"
            placeholder="e.g., Singing, Dancing"
            value={formData.extra_skills}
            onChange={handleUpdateFormData}
            error={errors.extra_skills}
          />
          {errors.extra_skills && <p className="text-red-500 text-sm mt-1">{errors.extra_skills}</p>}
        </div>
      </div>
      <div className="flex justify-center gap-3 mt-6 w-full px-2">
        <button
          className="flex-1 py-3 bg-[#E72848] text-white rounded-[10px] h-[45px] font-semibold hover:bg-[#f53b58] transition duration-300 cursor-pointer"
          onClick={handleNext}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default PersonalDetails;